import { useCallback, useMemo, useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import { useApi } from "@/hooks/useApi";
import { apiPost } from "@/lib/api";
import type { AttendData } from "@/lib/types";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import AttendCalendar from "./components/AttendCalendar";

function currentYm(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function shiftYm(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function ymLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${y}년 ${Number(m)}월`;
}

export default function AttendPage() {
  const { member, requireLogin } = useAuth();
  const { toast } = useToast();
  const [ym, setYm] = useState(currentYm());
  const [checking, setChecking] = useState(false);

  const { data, loading, error, reload } = useApi<AttendData>(`attend.php?ym=${ym}`);

  const todayCnt = data?.today_cnt ?? 0;
  const myMonthCnt = data?.my_month_cnt ?? 0;
  const myToday = data?.my_today === 1;
  const todayList = useMemo(() => data?.today_list ?? [], [data]);
  const rank = useMemo(() => data?.rank ?? [], [data]);
  const myDays = useMemo(() => data?.my_days ?? [], [data]);
  const byDay = useMemo(() => data?.by_day ?? {}, [data]);

  const isCurrentMonth = ym === currentYm();

  const handleCheck = useCallback(async () => {
    if (!requireLogin()) return;
    setChecking(true);
    try {
      const res = await apiPost<{ today_cnt: number; my_month_cnt: number }>(
        "attend_check.php",
        {}
      );
      toast(res.msg || "출근도장을 찍었습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "도장을 찍지 못했습니다.", "error");
    } finally {
      setChecking(false);
    }
  }, [requireLogin, toast, reload]);

  return (
    <>
      <PageBanner
        title="출근도장"
        description="매일 출근도장을 찍고 회원들의 활동을 확인하세요."
        breadcrumb={[{ label: "출근도장" }]}
      />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        {/* 월 이동 + 도장 찍기 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setYm((v) => shiftYm(v, -1))}
              aria-label="이전 달"
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100"
            >
              <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
            </button>
            <span className="min-w-[120px] text-center font-heading text-sm font-bold text-foreground-950">
              {ymLabel(ym)}
            </span>
            <button
              type="button"
              onClick={() => setYm((v) => shiftYm(v, 1))}
              aria-label="다음 달"
              className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100"
            >
              <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
            </button>
          </div>

          <button
            type="button"
            onClick={handleCheck}
            disabled={myToday || checking || !isCurrentMonth}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3 text-sm font-bold transition-colors ${
              myToday
                ? "bg-background-200 text-foreground-500"
                : "bg-accent-500 text-primary-950 hover:bg-accent-600"
            } disabled:cursor-not-allowed`}
          >
            <i
              className={myToday ? "ri-checkbox-circle-fill" : "ri-fingerprint-line"}
              aria-hidden="true"
            ></i>
            {myToday ? "오늘 도장 완료" : checking ? "찍는 중…" : "출근도장 찍기"}
          </button>
        </div>

        {/* 요약 */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
            <div className="font-heading text-2xl md:text-3xl font-bold text-primary-700">
              {todayCnt}
              <span className="ml-0.5 text-base font-semibold">명</span>
            </div>
            <div className="mt-1 text-sm text-foreground-500">오늘 출근</div>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
            <div className="font-heading text-2xl md:text-3xl font-bold text-secondary-800">
              {myMonthCnt}
              <span className="ml-0.5 text-base font-semibold">일</span>
            </div>
            <div className="mt-1 text-sm text-foreground-500">이번 달 내 출근</div>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
            <div className="font-heading text-2xl md:text-3xl font-bold text-accent-600">
              {member ? "출근 중" : "미로그인"}
            </div>
            <div className="mt-1 text-sm text-foreground-500">
              {member ? `${member.name}님` : "로그인 후 도장을 찍을 수 있어요"}
            </div>
          </div>
        </div>

        {/* 본문 */}
        {loading ? (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-80 w-full rounded-xl lg:col-span-2" />
            <Skeleton className="h-80 w-full rounded-xl" />
          </div>
        ) : error ? (
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="출근 정보를 불러오지 못했습니다"
              description={error}
              action={
                <button
                  type="button"
                  onClick={reload}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  다시 시도
                </button>
              }
            />
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 달력 */}
            <div className="lg:col-span-2 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                {ymLabel(ym)} 출근 현황
              </h2>
              <AttendCalendar ym={ym} byDay={byDay} myDays={myDays} />
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded bg-accent-500 border border-accent-400" aria-hidden="true"></span>
                  내 출근
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded border border-background-300 bg-background-50" aria-hidden="true"></span>
                  출근 인원 표시
                </span>
              </div>
            </div>

            {/* 오늘 출근 + 랭킹 */}
            <div className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                  오늘 출근한 회원
                </h2>
                {todayList.length === 0 ? (
                  <p className="py-6 text-center text-sm text-foreground-400">
                    아직 출근한 회원이 없습니다.
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-3">
                    {todayList.map((p, i) => (
                      <li key={`${p.name}-${i}`} className="flex flex-col items-center gap-1 w-14">
                        <Avatar name={p.name || "회원"} photo={p.photo} size={44} />
                        <span className="w-full truncate text-center text-[11px] text-foreground-600">
                          {[p.name, p.aho].filter(Boolean).join(" ") || "회원"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                  이번 달 출근 랭킹
                </h2>
                {rank.length === 0 ? (
                  <p className="py-6 text-center text-sm text-foreground-400">
                    아직 집계된 출근 기록이 없습니다.
                  </p>
                ) : (
                  <ol className="space-y-3">
                    {rank.map((r, i) => (
                      <li key={`${r.name}-${i}`} className="flex items-center gap-3">
                        <span
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                            r.rank <= 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-100 text-foreground-600"
                          }`}
                        >
                          {r.rank}
                        </span>
                        <Avatar name={r.name || "회원"} photo={r.photo} size={32} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-800">
                          {r.name}
                          {r.aho && (
                            <span className="ml-1 text-xs text-foreground-400">({r.aho})</span>
                          )}
                        </span>
                        <span className="shrink-0 text-sm font-semibold text-secondary-800">
                          {r.cnt}일
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}